import { Component } from '@theme/component';

// icon-arrow-right.svg (inliné : pas d'accès aux assets liquid côté JS)
const ARROW =
  '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden="true" focusable="false"><path d="M11.0078 0.353271L15.9998 5.35327L11.0078 10.3533" stroke="currentColor" stroke-miterlimit="10"/><path d="M16 5.35327H0" stroke="currentColor" stroke-miterlimit="10"/></svg>';

/**
 * Ax Ingredients — page « Les meilleurs actifs ».
 *
 * Deux onglets (Ingrédients / Fournisseurs), chacun avec sa grille de cartes rendues
 * côté Liquid depuis les métaobjets. Pagination client-side (masque/affiche les cartes
 * par page) qui réutilise les classes `.ax-pagination__page` pour être strictement
 * identique à la pagination blog/collection. « Lire la suite » retire le clamp CSS.
 *
 * @extends {Component<{ tabs: HTMLElement[], panels: HTMLElement[], grids: HTMLElement[], paginations: HTMLElement[] }>}
 */
class AxIngredients extends Component {
  #perPage = 12;
  #page = { ingredients: 1, suppliers: 1 };

  connectedCallback() {
    super.connectedCallback();
    this.#perPage = parseInt(this.dataset.perPage, 10) || 12;

    for (const text of this.querySelectorAll('.ax-ingredients__text')) {
      text.classList.add('ax-ingredients__text--clamp');
    }

    for (const tab of ['ingredients', 'suppliers']) {
      this.#showPage(tab, 1);
    }

    this.#initKeyboard();
  }

  // ── Onglets ──
  /** @param {string} tab */
  selectTab(tab) {
    for (const t of this.refs.tabs ?? []) {
      const active = t.getAttribute('aria-controls') === `ax-ingredients-panel-${tab}`;
      t.classList.toggle('ax-ingredients__tab--active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.tabIndex = active ? 0 : -1;
    }
    for (const panel of this.refs.panels ?? []) {
      panel.hidden = panel.dataset.tab !== tab;
    }
    // Ré-évalue le « Lire la suite » maintenant que le panneau est visible (mesure impossible en [hidden]).
    this.#showPage(tab, this.#page[tab]);
  }

  #initKeyboard() {
    const tabs = this.refs.tabs ?? [];
    tabs.forEach((tab, i) => {
      tab.addEventListener('keydown', (event) => {
        const map = { ArrowRight: tabs[(i + 1) % tabs.length], ArrowLeft: tabs[(i - 1 + tabs.length) % tabs.length], Home: tabs[0], End: tabs[tabs.length - 1] };
        const next = map[event.key];
        if (!next) return;
        event.preventDefault();
        next.click();
        next.focus();
      });
    });
  }

  // ── Lire la suite ──
  /** @param {Event} event */
  toggleMore(event) {
    const wrap = event.target.closest('.ax-ingredients__text-wrap');
    if (!wrap) return;
    wrap.querySelector('.ax-ingredients__text')?.classList.remove('ax-ingredients__text--clamp');
    const button = wrap.querySelector('.ax-ingredients__more');
    if (button) button.hidden = true;
  }

  /** @param {HTMLElement} card */
  #evaluateReadMore(card) {
    const wrap = card.querySelector('.ax-ingredients__text-wrap');
    if (!wrap || wrap.dataset.evaluated) return;
    const text = wrap.querySelector('.ax-ingredients__text');
    const button = wrap.querySelector('.ax-ingredients__more');
    if (!text || !button) return;
    requestAnimationFrame(() => {
      if (text.scrollHeight - text.clientHeight > 1) button.hidden = false;
      wrap.dataset.evaluated = '1';
    });
  }

  // ── Pagination client-side (classes .ax-pagination__page = iso blog/collection) ──
  #grid(tab) {
    return (this.refs.grids ?? []).find((grid) => grid.dataset.tab === tab);
  }

  #nav(tab) {
    return (this.refs.paginations ?? []).find((nav) => nav.dataset.tab === tab);
  }

  #cards(tab) {
    const grid = this.#grid(tab);
    return grid ? /** @type {HTMLElement[]} */ ([...grid.children]) : [];
  }

  /** @param {string} tab @param {number} page */
  #showPage(tab, page) {
    const cards = this.#cards(tab);
    const count = Math.max(1, Math.ceil(cards.length / this.#perPage));
    page = Math.min(Math.max(1, page), count);
    this.#page[tab] = page;

    const start = (page - 1) * this.#perPage;
    const end = start + this.#perPage;

    cards.forEach((card, i) => {
      const visible = i >= start && i < end;
      card.hidden = !visible;
      if (visible) this.#evaluateReadMore(card);
    });

    this.#renderPagination(tab, count);
  }

  /** @param {string} tab @param {number} count */
  #renderPagination(tab, count) {
    const nav = this.#nav(tab);
    if (!nav) return;

    if (count <= 1) {
      nav.hidden = true;
      nav.replaceChildren();
      return;
    }
    nav.hidden = false;

    const current = this.#page[tab];
    const fragment = document.createDocumentFragment();

    if (current > 1) fragment.append(this.#arrow(tab, current - 1, 'prev'));

    for (const entry of this.#window(current, count)) {
      if (entry === '…') {
        const span = document.createElement('span');
        span.className = 'ax-pagination__page ax-pagination__page--gap';
        span.textContent = '…';
        fragment.append(span);
      } else if (entry === current) {
        const span = document.createElement('span');
        span.className = 'ax-pagination__page ax-pagination__page--active';
        span.setAttribute('aria-current', 'page');
        span.textContent = String(entry);
        fragment.append(span);
      } else {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ax-pagination__page';
        button.textContent = String(entry);
        button.addEventListener('click', () => this.#goToPage(tab, entry));
        fragment.append(button);
      }
    }

    if (current < count) fragment.append(this.#arrow(tab, current + 1, 'next'));

    nav.replaceChildren(fragment);
  }

  /** @param {string} tab @param {number} target @param {'prev'|'next'} dir */
  #arrow(tab, target, dir) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ax-pagination__page ax-pagination__page--nav' + (dir === 'prev' ? ' ax-pagination__page--prev' : '');
    const label = dir === 'prev' ? this.dataset.labelPrev : this.dataset.labelNext;
    if (label) button.setAttribute('aria-label', label);
    button.innerHTML = ARROW;
    button.addEventListener('click', () => this.#goToPage(tab, target));
    return button;
  }

  /** @param {string} tab @param {number} page */
  #goToPage(tab, page) {
    this.#showPage(tab, page);
    this.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  /** Fenêtre : 1 … courant-1 courant courant+1 … N @param {number} current @param {number} count */
  #window(current, count) {
    const wanted = new Set([1, count, current - 1, current, current + 1]);
    const pages = [...wanted].filter((n) => n >= 1 && n <= count).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of pages) {
      if (n - prev > 1) out.push('…');
      out.push(n);
      prev = n;
    }
    return out;
  }
}

if (!customElements.get('ax-ingredients')) {
  customElements.define('ax-ingredients', AxIngredients);
}
